import React, { useEffect, useMemo, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faEllipsisV,
  faFileContract,
} from "@fortawesome/free-solid-svg-icons";

import { useAppData } from "../../../context/AppDataContext";
import DragDrop from "../../DragDrop.jsx";
import FilePreview from "../../files/FilePreview.jsx";
import ConfirmDelete from "../../modals/ConfirmDelete.jsx";
import FormFieldProjects from "../../input/FormFieldProjects.jsx";
import {
  errorToast,
  successfulToast,
} from "../../../util/toastNotifications.jsx";
import { setCachedBreadcrumbLabel } from "../../../helpers/breadcrumbCache";

const ContractDetailPage = () => {
  const { contractId } = useParams();
  const { api } = useAppData();
  const history = useHistory();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isUploadCollapsed, setIsUploadCollapsed] = useState(true);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);
  const files = contract?.files || [];

  useEffect(() => {
    const fetchContractData = async () => {
      try {
        setLoading(true);
        const contractData = await api.contract.getContractById(contractId);
        setContract(contractData);
        setUploadedFiles(contractData?.files || []);
      } catch (error) {
        console.error("Error fetching contract data:", error);
        errorToast("Failed to load contract");
      } finally {
        setLoading(false);
      }
    };

    fetchContractData();
  }, [api, contractId]);

  useEffect(() => {
    if (contract?.contract_id && contract?.name) {
      setCachedBreadcrumbLabel("contract", contract.contract_id, contract.name);
    }
  }, [contract]);

  useEffect(() => {
    if (contract?.files) {
      setUploadedFiles(contract.files);
    }
  }, [contract]);

  useEffect(() => {
    setUploadedFiles(contract?.files || []);
  }, [contract]);

  const formatDate = (value) =>
    value ? new Date(value).toLocaleDateString() : "Not set";

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "Not set";
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return "Not set";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(numericValue);
  };

  const openPreview = (file) => {
    setPreviewFile(file);
  };

  const closePreview = () => {
    if (previewFile?.previewUrl) {
      URL.revokeObjectURL(previewFile.previewUrl);
    }
    setPreviewFile(null);
  };

  const handleContractFileUpload = async ({ file, preview_url }) => {
    const normalize = (name) => name.trim().toLowerCase();
    const existingFile = (uploadedFiles || []).find(
      (f) => normalize(f.file_name) === normalize(file.name)
    );
    if (existingFile) {
      errorToast("A file with this name already exists in the contract.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("org_id", contract.org_id);

    try {
      const response = await api.file.createFiles(formData);
      const uploadedFile = response.results;

      await api.contract.toggleFile(contractId, uploadedFile.file_id);

      setUploadedFiles((prev) => [...prev, { ...uploadedFile, preview_url }]);
      successfulToast("File uploaded successfully");
    } catch (err) {
      console.error("Contract file upload failed", err);
      errorToast("Failed to upload file");
    }
  };

  const handleRemoveFile = async (file) => {
    try {
      await api.file.deleteFile(file.file_id);
      if (file.previewUrl) {
        URL.revokeObjectURL(file.preview_url);
      }

      setUploadedFiles((prev) =>
        prev.filter((f) => f.file_id !== file.file_id)
      );
      successfulToast("File removed successfully");
    } catch (err) {
      console.error("Failed to delete file", err);
      errorToast("Failed to remove file");
    }
  };

  const handleProjectRemove = async (projectId) => {
    try {
      await api.project.updateProject(projectId, { contract_id: null });
      setContract((prev) => ({
        ...prev,
        projects: (prev?.projects || []).filter(
          (project) => project.project_id !== projectId
        ),
      }));
      successfulToast("Project removed from contract");
    } catch (error) {
      console.error("Error removing project from contract:", error);
      errorToast("Failed to remove project from contract");
    }
  };

  const handleAddProjectToContract = (newProject) => {
    setContract((prev) => {
      const existingProjects = prev?.projects || [];
      const alreadyLinked = existingProjects.some(
        (project) => project.project_id === newProject.project_id
      );
      if (alreadyLinked) {
        return prev;
      }
      return {
        ...prev,
        projects: [...existingProjects, newProject],
      };
    });
    successfulToast("Project added to contract");
  };

  const toggleDetails = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleEditContract = () => {
    setIsDropdownOpen(false);
    history.push(`/contract-form/${contractId}`);
  };

  if (loading) {
    return <div className="loading">Loading contract...</div>;
  }

  if (!contract) {
    return <div>Contract not found</div>;
  }

  return (
    <div className="width-full max-width-full overflow-hidden">
      <div className="detail-header">
        <div className="header-main">
          <h1
            style={{
              borderBottom: "2px solid #6e747a",
            }}
          >
            <FontAwesomeIcon
              icon={faFileContract}
              className="mr-2"
              style={{ color: "#6e747a" }}
            />
            {contract.name}
          </h1>
          <div className="action-buttons">
            <FontAwesomeIcon
              icon={faEllipsisV}
              className="dropdown-toggle"
              data-bs-toggle="dropdown"
              style={{ marginBottom: "6px" }}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            />
          </div>
          {isDropdownOpen && (
            <div className="dropdown-menu show">
              <button className="dropdown-item" onClick={handleEditContract}>
                Edit Contract
              </button>
              <ConfirmDelete objectType="contract" id={contractId} />
            </div>
          )}
        </div>

        <div className="header-info">
          <div className="info-item">
            <span className="info-label">Organization</span>
            <span className="info-value">
              {contract.organization?.name || "N/A"}
            </span>
          </div>

          <div className="info-item">
            <span className="info-label">Company</span>
            <span className="info-value">
              {contract.company ? (
                <a href={`/company/${contract.company.company_id}`}>
                  {contract.company.name}
                </a>
              ) : (
                "N/A"
              )}
            </span>
          </div>

          <div className="info-item">
            <span className="info-label">Contact</span>
            <span className="info-value">
              {contract.contact ? (
                <a href={`/contact/${contract.contact.user_id}`}>
                  {contract.contact.first_name} {contract.contact.last_name}
                </a>
              ) : (
                "N/A"
              )}
            </span>
          </div>

          <div className="info-item">
            <span className="info-label">PO Number</span>
            <span className="info-value">{contract.po_number || "N/A"}</span>
          </div>

          <div className="info-item">
            <span className="info-label">Contract Value</span>
            <span className="info-value">
              {formatCurrency(contract.max_contract_amount)}
            </span>
          </div>

          <div className="info-item">
            <span className="info-label">Projects</span>
            <div className="info-value">
              <FormFieldProjects
                hideLabel
                selectedProjects={contract.projects || []}
                contractId={contract.contract_id}
                handleProjectRemove={handleProjectRemove}
                handleAddProjectToContract={handleAddProjectToContract}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-4 detail-header">
        <h3 className="d-flex">
          Contract Detail
          <span
            className="ml-2 chevron-toggle"
            onClick={toggleDetails}
            aria-label={isCollapsed ? "Expand details" : "Collapse details"}
          >
            <FontAwesomeIcon icon={isCollapsed ? faChevronDown : faChevronUp} />
          </span>
        </h3>
        {!isCollapsed && (
          <div className="detail-expanded">
            <div className="info-item description-item">
              <span className="info-label">Description</span>
              <div className="info-value">
                {contract.description || "No description provided."}
              </div>
            </div>

            <div className="expanded-info">
              <div className="info-item">
                <span className="info-label">Contract Number</span>
                <span className="info-value">
                  {contract.contract_number || "N/A"}
                </span>
              </div>

              <div className="info-item">
                <span className="info-label">Invoice Email</span>
                <span className="info-value">
                  {contract.invoice_email || "N/A"}
                </span>
              </div>

              <div className="info-item">
                <span className="info-label">Start Date</span>
                <span className="info-value">
                  {formatDate(contract.start_date)}
                </span>
              </div>

              <div className="info-item">
                <span className="info-label">End Date</span>
                <span className="info-value">
                  {formatDate(contract.end_date)}
                </span>
              </div>

              <div className="info-item">
                <span className="info-label">Created</span>
                <span className="info-value">
                  {formatDate(contract.date_created)}
                </span>
              </div>

              <div className="info-item">
                <span className="info-label">Last Updated</span>
                <span className="info-value">
                  {formatDate(contract.date_updated)}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="card mb-4 detail-header">
        <h3 className="d-flex">
          Documentation & Files
          <span
            className="ml-2 chevron-toggle"
            onClick={() => {
              setIsUploadCollapsed(!isUploadCollapsed);
            }}
          >
            <FontAwesomeIcon
              icon={isUploadCollapsed ? faChevronDown : faChevronUp}
            />
          </span>
        </h3>
        {!isUploadCollapsed && (
          <div className="detail-expanded">
            <div>
              <DragDrop
                onSuccessfulUpload={handleContractFileUpload}
                accept="*/*"
                multiple={true}
                maxSize={20_000_000}
                uploadedFiles={uploadedFiles}
                setUploadedFiles={setUploadedFiles}
              />
            </div>
            <div>
              {uploadedFiles.length > 0 ? (
                <div className="uploaded-files">
                  <p className="file-upload-message">
                    File{uploadedFiles.length > 1 ? "s" : ""} uploaded
                    successfully:
                  </p>
                  <ul>
                    {uploadedFiles.map((file) => (
                      <li key={file.file_id} className="file-display-wrapper">
                        <span
                          className="file-name"
                          onClick={() => openPreview(file)}
                        >
                          {file.file_name}
                        </span>

                        <button
                          className="remove-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFile(file);
                          }}
                        >
                          x
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="info-value">No files uploaded yet.</div>
              )}
            </div>

            <div>
              <FilePreview file={previewFile} onClose={closePreview} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContractDetailPage;
